import { Injectable } from '@angular/core';
import { BookmarkRecord } from 'fl-bookmarks';
import { resourceAdaptor } from 'qp-adaptor';
import { ContainerVm, Resource, ResourceVm } from 'qp-ui-interface';
import {
  isEpisodeType,
  isSeriesType,
} from 'src/app/shared/common/const/common.const';
import { SeriesData } from 'src/app/shared/common/interfaces/common.interface';
import { ContentType } from 'src/app/shared/enums/contentType';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { RecommendationsService } from 'src/app/shared/services/firstlight/recommendations/recommendations.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { BookmarkPayload } from '../../shared/interfaces/bookmark';
import { ErrorFactory } from '../../shared/modules/errors/handler/ErrorFactory';
import { BookmarkService } from '../../shared/services/bookmark.service';
import { ConfigService } from '../../shared/services/config.service';
import { FavouriteService } from '../../shared/services/favourite.service';
import { MetaDataService } from '../../shared/services/meta-data.service';
import { AppConfig } from '../../shared/utils/constants/app-config.constant';
import { getDeviceType } from '../../shared/utils/deviceUtils';
import { ErrorEvents } from '../../shared/utils/ErrorUtils';

@Injectable()
export class DetailPageService {
  private readonly applicationConfig: AppConfig;

  constructor(
    private readonly configService: ConfigService,
    private readonly metadataService: MetaDataService,
    private readonly bookmarkService: BookmarkService,
    private readonly authService: AuthService,
    private readonly recommendationsService: RecommendationsService,
    private readonly favouriteService: FavouriteService,
    private toast: ToastService
  ) {
    this.applicationConfig = this.configService.applicationConfig;
  }

  public async fetchSeries(
    resourceId: string,
    pageNumber: number = 1,
    pageSize: number = 30,
    path: string = 'webseries',
    seriesPath: string = 'webseasons'
  ): Promise<ResourceVm[]> {
    try {
      const response = await this.metadataService.getSeries(
        resourceId,
        pageNumber,
        pageSize,
        path,
        seriesPath
      );
      const resources: Resource[] = response?.resources;
      return resources.map((res: Resource) =>
        resourceAdaptor(
          this.applicationConfig.imageResizeURL,
          res,
          getDeviceType(),
          '0-16x9'
        )
      );
    } catch (error) {
      console.warn('Series fetch error', error);
      return null;
    }
  }

  public async fetchEpisodes(
    seriesId: string,
    seasonId: string,
    seasonNumber: number,
    pageNumber: number = 1,
    pageSize: number = 20,
    seriesPath: string = 'webseries/',
    episodesPath: string = 'webepisodes',
    storefrontId?: string,
    customData?: { tabId: string; tabName: string; originScreen?: string }
  ): Promise<ContainerVm> {
    try {
      if (!customData) {
        customData = {
          tabId: '',
          tabName: '',
          originScreen: '',
        };
      }
      const { tabId, tabName, originScreen } = customData;
      const response = await this.metadataService.getEpisodes(
        seriesId,
        seasonId,
        pageNumber.toString(),
        pageSize.toString(),
        seriesPath,
        episodesPath
      );
      const resources: Resource[] = response?.resources;
      const containerVm: ContainerVm = {
        id: seriesId,
        aspectRatio: '16x9',
        name: `Season ${seasonNumber || 1}`,
        layout: 'carousel',
        resources: resources?.map((res: Resource) =>
          resourceAdaptor(
            this.applicationConfig.imageResizeURL,
            res,
            getDeviceType(),
            '0-16x9',
            undefined,
            undefined,
            {
              storefrontId,
              tabId,
              tabName,
              originScreen,
            }
          )
        ),
      };

      containerVm.totalItems = response?.totalResources;
      return containerVm;
    } catch (error) {
      return Promise.reject(
        ErrorFactory.get(error, ErrorEvents.EPISODES_FETCH_ERROR)
      );
    }
  }

  async fetchSeriesData(
    resource: ResourceVm,
    bookmark: BookmarkRecord | null,
    episodePageNumber: number,
    episodePageSize: number
  ): Promise<SeriesData | null> {
    const isOfSeriesType = isSeriesType.includes(
      resource.contentType as ContentType
    );

    const seriesId = isOfSeriesType ? resource.id : resource.seriesId;
    const seasons = await this.fetchSeries(seriesId);

    if (!seasons?.length) {
      return null;
    }

    const activeSeason = this.determineActiveSeason(
      seasons,
      resource,
      bookmark,
      isOfSeriesType
    );
    if (!activeSeason) {
      return null;
    }

    const episodes = await this.fetchEpisodes(
      seriesId,
      activeSeason.id,
      activeSeason.seasonNumber,
      episodePageNumber,
      episodePageSize
    );

    const activeEpisode = this.determineActiveEpisode(
      episodes.resources,
      resource,
      bookmark,
      isOfSeriesType
    );

    return {
      seasons,
      episodes,
      activeSeasonId: activeSeason.id,
      activeEpisode,
    };
  }

  private determineActiveSeason(
    seasons: ResourceVm[],
    resource: ResourceVm,
    bookmark: BookmarkRecord | null,
    isOfSeriesType: boolean
  ): ResourceVm | null {
    if (isOfSeriesType) {
      return bookmark
        ? seasons.find((season) => season.id === bookmark.seasonId)
        : seasons[0];
    }

    return seasons.find((season) => season.id === resource.seasonId) ?? null;
  }

  private determineActiveEpisode(
    episodes: ResourceVm[],
    resource: ResourceVm,
    bookmark: BookmarkRecord | null,
    isOfSeriesType: boolean
  ): ResourceVm | null {
    if (bookmark?.episodeId) {
      return (
        episodes.find((episode) => episode.id === bookmark.episodeId) ?? null
      );
    }

    if (isOfSeriesType) {
      return episodes[0] ?? null;
    }

    return episodes.find((episode) => episode.id === resource.id) ?? null;
  }

  async putBookmark(payload: BookmarkPayload) {
    return await this.bookmarkService.putBookmark(payload);
  }

  async getBookmark(bookmarkContentId: string) {
    if (this.authService.isLoggedIn()) {
      await new Promise((resolve) => setTimeout(resolve, 250));
      return await this.bookmarkService.getBookmark(bookmarkContentId);
    }
    return null;
  }

  async fetchMoreLikeThis(resourceId: string, contentType: string) {
    return await this.recommendationsService.fetchMoreLikeThis(
      resourceId,
      contentType,
      '2x3'
    );
  }

  async fetchMostPopular(contentType: ContentType) {
    return await this.recommendationsService.fetchMostPopular(contentType);
  }

  async fetchFavourite(resourceId: string) {
    if (this.authService.isLoggedIn()) {
      return await this.favouriteService.getFavourite(resourceId);
    }
    return null;
  }

  async fetchPageData(resource: ResourceVm) {
    const contentId = isEpisodeType.includes(
      resource.contentType as ContentType
    )
      ? resource.seriesId
      : resource.id;
    const [favourite, moreLikeThis, mostPopular] = await Promise.all([
      this.fetchFavourite(resource.id),
      this.fetchMoreLikeThis(contentId, resource.contentType),
      this.fetchMostPopular(resource.contentType as ContentType),
    ]);
    delete moreLikeThis.localizedName;
    return {
      favourite,
      moreLikeThis: moreLikeThis?.resources?.length > 0 ? moreLikeThis : null,
      mostPopular: mostPopular?.resources?.length > 0 ? mostPopular : null,
    };
  }

  async updateFavourite(resourceId: string, isFavourite: boolean) {
    const response = isFavourite
      ? await this.favouriteService.putFavourite(resourceId)
      : await this.favouriteService.deleteFavourite(resourceId);

    if (!response) return null;

    if (isFavourite) {
      this.toast.success('added_to_watchlist');
      return await this.favouriteService.getFavourite(resourceId);
    } else {
      this.toast.remove('removed_from_watchlist');
      return false;
    }
  }

  watchPercent(bkmarkOffset: number, resourceOffset: number) {
    return this.bookmarkService.watchPercent(bkmarkOffset, resourceOffset);
  }
}
